import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, X } from 'lucide-react';
import { apiService } from '../../services';
import { isHorsSerie } from '../../context';
import { Article, Event, Album, Book, Canticle } from '../../types';

type SelectedType = 'articles' | 'events' | 'albums' | 'canticles' | 'books';

interface EntityCardProps {
  item: Article | Event | Album | Book | Canticle;
  type: SelectedType;
  onEdit: () => void;
  onDelete: () => void;
}

export const EntityCard: React.FC<EntityCardProps> = ({
  item,
  type,
  onEdit,
  onDelete,
}) => {
  const navigate = useNavigate();

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger edit if clicking delete or preview button
    if ((e.target as HTMLElement).closest('.delete-button, .preview-button')) {
      return;
    }
    onEdit();
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete();
  };

  const handlePreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    const previewUrl = type === 'articles' 
      ? `/news/${item.id}`
      : type === 'events'
      ? `/events/${item.id}`
      : null;
    
    if (previewUrl) {
      navigate(previewUrl);
    }
  };

  const renderContent = () => {
    switch (type) {
      case 'articles': {
        const article = item as Article;
        return (
          <div className='relative h-full flex flex-col'>
            {/* Action Buttons */}
            <div className='absolute top-2 right-2 z-10 flex gap-1'>
              <button
                onClick={handlePreview}
                className='preview-button bg-blue-500 text-white rounded-full w-7 h-7 flex items-center justify-center hover:bg-blue-600 transition-colors shadow-md'
                title='Prévisualiser'
              >
                <Eye className='w-4 h-4' />
              </button>
              <button
                onClick={handleDelete}
                className='delete-button bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center hover:bg-red-600 transition-colors shadow-md'
                title='Supprimer'
              >
                <X className='w-4 h-4' />
              </button>
            </div>

            {/* Image */}
            {article.image_path && (
              <div className='w-full h-48 overflow-hidden rounded-t-lg bg-gray-100'>
                <img
                  src={apiService.getArticleImageUrl(article.id)}
                  alt={article.title}
                  className='w-full h-full object-cover'
                  loading='lazy'
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            )}

            {/* Content */}
            <div className='p-4 flex-1 flex flex-col'>
              <h3 className='font-semibold text-gray-900 mb-2 line-clamp-2'>
                {article.title}
              </h3>
              <div className='mt-auto flex items-center justify-between text-sm'>
                <span
                  className={`px-2 py-1 rounded text-xs ${
                    article.status === 'published'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {article.status}
                </span>
                {article.published_at && (
                  <span className='text-gray-500'>
                    {new Date(article.published_at).toLocaleDateString('fr-FR')}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      }

      case 'events': {
        const event = item as Event;
        return (
          <div className='relative h-full flex flex-col'>
            {/* Action Buttons */}
            <div className='absolute top-2 right-2 z-10 flex gap-1'>
              <button
                onClick={handlePreview}
                className='preview-button bg-blue-500 text-white rounded-full w-7 h-7 flex items-center justify-center hover:bg-blue-600 transition-colors shadow-md'
                title='Prévisualiser'
              >
                <Eye className='w-4 h-4' />
              </button>
              <button
                onClick={handleDelete}
                className='delete-button bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center hover:bg-red-600 transition-colors shadow-md'
                title='Supprimer'
              >
                <X className='w-4 h-4' />
              </button>
            </div>

            {/* Image */}
            {event.image_path && (
              <div className='w-full h-48 overflow-hidden rounded-t-lg bg-gray-100'>
                <img
                  src={apiService.getEventImageUrl(event.id)}
                  alt={event.title}
                  className='w-full h-full object-cover'
                  loading='lazy'
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            )}

            {/* Content */}
            <div className='p-4 flex-1 flex flex-col'>
              <h3 className='font-semibold text-gray-900 mb-2 line-clamp-2'>
                {event.title}
              </h3>
              <div className='mt-auto space-y-1'>
                <div className='flex items-center justify-between text-sm'>
                  <span
                    className={`px-2 py-1 rounded text-xs ${
                      event.status === 'published'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {event.status}
                  </span>
                  {event.published_at && (
                    <span className='text-gray-500'>
                      {new Date(event.published_at).toLocaleDateString('fr-FR')}
                    </span>
                  )}
                </div>
                {event.event_date && (
                  <div className='text-xs text-gray-600'>
                    <span className='font-medium'>Date:</span>{' '}
                    {new Date(event.event_date).toLocaleDateString('fr-FR')}
                    {event.event_date.includes('T') && (
                      <>
                        {' '}
                        <span className='font-medium'>Heure:</span>{' '}
                        {new Date(event.event_date).toLocaleTimeString('fr-FR', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      }

      case 'canticles': {
        const canticle = item as Canticle;
        return (
          <div className='relative h-full flex flex-col'>
            {/* Delete Button */}
            <button
              onClick={handleDelete}
              className='delete-button absolute top-2 right-2 z-10 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center hover:bg-red-600 transition-colors shadow-md'
              title='Supprimer'
            >
              <X className='w-4 h-4' />
            </button>

            {/* Content */}
            <div className='p-4 flex-1 flex flex-col'>
              <h3 className='font-semibold text-gray-900 mb-2 line-clamp-2'>
                {canticle.title}
              </h3>
              <div className='mt-auto'>
                {canticle.published_at && (
                  <span className='text-sm text-gray-500'>
                    {new Date(canticle.published_at).toLocaleDateString('fr-FR')}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      }

      case 'books': {
        const book = item as Book;
        return (
          <div className='relative h-full flex flex-col'>
            {/* Delete Button */}
            <button
              onClick={handleDelete}
              className='delete-button absolute top-2 right-2 z-10 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center hover:bg-red-600 transition-colors shadow-md'
              title='Supprimer'
            >
              <X className='w-4 h-4' />
            </button>

            {/* Content */}
            <div className='p-4 flex-1 flex flex-col'>
              {isHorsSerie(book) ? (
                <span className='text-xs font-semibold text-rose-600 uppercase tracking-wide mb-1'>
                  Hors-série
                </span>
              ) : (
                <span className='text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1'>
                  N° {book.edition_num}
                </span>
              )}
              <h3 className='font-semibold text-gray-900 mb-2 line-clamp-2'>
                {book.title}
              </h3>
              <div className='mt-auto flex items-center justify-between gap-2'>
                <span className='text-sm text-gray-500'>
                  {new Date(book.created_at).toLocaleDateString('fr-FR')}
                </span>
                {book.price != null && (
                  <span className='text-sm font-semibold text-amber-700 whitespace-nowrap'>
                    {book.price} {book.currency === 'EUR' ? '€' : book.currency}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div
      onClick={handleCardClick}
      className='bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer h-full'
    >
      {renderContent()}
    </div>
  );
};
